import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { chartDateRangeOptions } from '@/modules/common/constants/chart-date-range-oprions.ts';
import { mapDateRangeToObj } from '@/modules/common/helpers/mapDateRangeToObj.ts';
import { normalizeChartData } from '@/modules/common/helpers/normalizeChartData.ts';
import { useTasksGraphicDataQuery } from '@/modules/common/queries/tasks.ts';
import { BalanceChart, SelectButtons, Skeleton } from 'ui';
import { ApexChartSeries } from 'ui/types';

const options = {
  tooltip: {
    custom: ({
      dataPointIndex,
      seriesIndex,
      w,
    }: {
      dataPointIndex: number;
      seriesIndex: number;
      w: any;
    }) => {
      const { x, y } = w.config.series[seriesIndex].data[dataPointIndex] || {};

      return renderToString(
        <div className="flex flex-col w-[250px] rounded-xl p-4 space-y-2 bg-white">
          <div>
            {w.config.series[seriesIndex].name} : {y}
          </div>
          <div className="w-full flex items-center justify-between">
            <p className="text-[14px] leading-[22px] font-medium">
              {dayjs(x).isValid() ? dayjs(x).format('DD-MM-YYYY') : ''}
            </p>
          </div>
        </div>,
      );
    },
  },
  fill: {},
  stroke: {
    curve: 'straight' as const,
    width: 2,
  },
  chart: {
    type: 'line' as const,
    toolbar: {
      show: false,
    },
  },
};

const TasksOverviewSkeleton = () => {
  return (
    <div className="w-full h-full pt-5">
      <div className="flex gap-5">
        <div className="flex gap-1.5">
          <Skeleton width={8} height={8} />
          <Skeleton width={96} height={8} />
        </div>
        <div className="flex gap-1.5">
          <Skeleton width={8} height={8} />
          <Skeleton width={96} height={8} />
        </div>
      </div>
      <div className="pt-4 space-y-14">
        {Array(5)
          .fill(null)
          .map((_, index) => (
            <div key={index} className="flex gap-3.5 w-full items-center">
              <Skeleton width={24} height={11} />
              <Skeleton height={1} />
            </div>
          ))}
      </div>
      <div className="flex justify-between mt-[-6px] pl-8">
        <div className="flex flex-col gap-2 items-center">
          <Skeleton width={1} height={12} />
          <Skeleton width={24} height={11} />
        </div>
        <div className="flex flex-col gap-2 items-center">
          <Skeleton width={1} height={12} />
          <Skeleton width={24} height={11} />
        </div>
        <div className="flex flex-col gap-2 items-center">
          <Skeleton width={1} height={12} />
          <Skeleton width={24} height={11} />
        </div>
        <div className="flex flex-col gap-2 items-center">
          <Skeleton width={1} height={12} />
          <Skeleton width={24} height={11} />
        </div>
      </div>
    </div>
  );
};

const TasksOverview = () => {
  const [dateRange, setDateRange] = useState(chartDateRangeOptions[0].value);

  const { data, isLoading, isFetching } = useTasksGraphicDataQuery(mapDateRangeToObj(dateRange));

  const chartData = useMemo<ApexChartSeries[]>(() => {
    const { completed_data = [], due_data = [] } = data || {};
    return [
      {
        name: 'Task completed',
        type: 'line',
        color: '#0369A1',
        data: normalizeChartData(
          completed_data.map(({ calculated_date, amount }) => ({
            x: calculated_date,
            y: amount,
          })),
        ),
      },
      {
        name: 'Task due',
        type: 'line',
        color: '#C588E9',
        data: normalizeChartData(
          due_data.map(({ calculated_date, amount }) => ({ x: calculated_date, y: amount })),
        ),
      },
    ];
  }, [data]);

  const isThereSomeChartData = chartData.some(({ data }) => data.length);

  return (
    <div className="border p-6 rounded-xl">
      <div className="flex justify-between flex-wrap">
        {isLoading ? (
          <Skeleton width={118} height={15} />
        ) : (
          <div className="font-medium text-lg">Tasks Overview</div>
        )}
        {isLoading ? (
          <Skeleton width={375} height={36} />
        ) : (
          <SelectButtons
            options={chartDateRangeOptions}
            selected={chartDateRangeOptions.find(({ value }) => value === dateRange)}
            onChange={setDateRange}
            className="sm:w-[375px] w-[300px]"
            disabled={isFetching}
          />
        )}
      </div>
      <div className="h-[350px] w-full flex items-center justify-center">
        {isFetching && <TasksOverviewSkeleton />}

        {!isFetching && !isThereSomeChartData && (
          <div className="w-full h-full flex items-center justify-center font-semibold text-2xl">
            You don't have upcoming tasks yet
          </div>
        )}

        {!isFetching && isThereSomeChartData && (
          <div className="w-full h-full">
            <BalanceChart data={chartData} type="line" options={options} />
          </div>
        )}
      </div>
    </div>
  );
};

export default TasksOverview;
